// Inline SVG flags for the language switcher.
//
// We deliberately avoid emoji flags (🇬🇪 / 🇬🇧): Windows has no regional-indicator
// glyphs, so they fall back to plain "GE"/"GB" letters. Inline SVG renders
// identically on every platform.

import { useId } from 'react'

type FlagProps = {
  className?: string
  title?: string
}

// Georgian "Five Cross Flag": white field, red St George's cross, and a small
// red bolnisi cross in each quadrant.
export const GeorgiaFlag = ({ className = '', title }: FlagProps) => {
  const clip = useId()
  return (
    <svg
      viewBox="0 0 60 40"
      className={className}
      role="img"
      aria-label={title || 'Georgian flag'}
      preserveAspectRatio="xMidYMid slice"
    >
      {title ? <title>{title}</title> : null}
      <clipPath id={clip}>
        <rect width="60" height="40" rx="4" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <rect width="60" height="40" fill="#fff" />
        {/* Central cross */}
        <rect x="24" y="0" width="12" height="40" fill="#E8112D" />
        <rect x="0" y="14" width="60" height="12" fill="#E8112D" />
        {/* Four bolnisi crosses (simplified) */}
        {[
          [12, 7],
          [48, 7],
          [12, 33],
          [48, 33],
        ].map(([cx, cy], i) => (
          <g key={i} fill="#E8112D">
            <rect x={cx - 1.5} y={cy - 4.5} width="3" height="9" />
            <rect x={cx - 4.5} y={cy - 1.5} width="9" height="3" />
          </g>
        ))}
      </g>
    </svg>
  )
}

// Union Jack (approximated for small sizes).
export const UKFlag = ({ className = '', title }: FlagProps) => {
  const clip = useId()
  return (
    <svg
      viewBox="0 0 60 40"
      className={className}
      role="img"
      aria-label={title || 'United Kingdom flag'}
      preserveAspectRatio="xMidYMid slice"
    >
      {title ? <title>{title}</title> : null}
      <clipPath id={clip}>
        <rect width="60" height="40" rx="4" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <rect width="60" height="40" fill="#012169" />
        {/* White diagonals */}
        <g stroke="#fff" strokeWidth="8">
          <line x1="0" y1="0" x2="60" y2="40" />
          <line x1="60" y1="0" x2="0" y2="40" />
        </g>
        {/* Red diagonals */}
        <g stroke="#C8102E" strokeWidth="3.5">
          <line x1="0" y1="0" x2="60" y2="40" />
          <line x1="60" y1="0" x2="0" y2="40" />
        </g>
        {/* White cross */}
        <rect x="24" y="0" width="12" height="40" fill="#fff" />
        <rect x="0" y="14" width="60" height="12" fill="#fff" />
        {/* Red cross */}
        <rect x="26.5" y="0" width="7" height="40" fill="#C8102E" />
        <rect x="0" y="16.5" width="60" height="7" fill="#C8102E" />
      </g>
    </svg>
  )
}
